import { useMemo } from 'react'
import { BasketThreshold } from '../../types/store/basket'
import { useBasketStore, useRapidDeliveryStore } from './useStore'

const THRESHOLD_SOURCE = {
    MIN_DELIVERY: 'minDelivery',
    FREE_DELIVERY: 'freeDelivery',
    SMALL_BASKET_CHARGE: 'smallBasketCharge',
    CUSTOM: 'custom',
    BASKET_LIMIT: 'basketLimit',
} as const

export const useThresholds = () => {
    const basketStore = useBasketStore()
    const rapidStore = useRapidDeliveryStore()
    const basketProductTotal = basketStore.basket?.productTotal || 0
    const basketDeliveryCharge = basketStore.basketDeliveryCharge
    const basketFreeDeliveryThreshold = basketStore.basketFreeDeliveryThreshold
    const basketSmallBasketChargeThreshold = basketStore.basketSmallBasketChargeThreshold
    const basketSmallBasketCharge = basketStore.basketSmallBasketCharge
    const basketMinDeliveryThreshold = basketStore.basketMinDeliveryThreshold
    const basketMaxItemsThreshold = basketStore.basketMaxItemsThreshold
    const basketMaxWeightThreshold = basketStore.basketMaxWeightThreshold
    const limitThreshold = basketStore.basketLimitThreshold
    const isRapidSlot = rapidStore.isRapidDeliveryBooked

    const getThresholdValue = (basketThreshold: BasketThreshold) => {
        switch (basketThreshold.thresholdSource) {
            case THRESHOLD_SOURCE.MIN_DELIVERY:
                return basketMinDeliveryThreshold || 0
            case THRESHOLD_SOURCE.FREE_DELIVERY:
                return basketFreeDeliveryThreshold || 0
            case THRESHOLD_SOURCE.SMALL_BASKET_CHARGE:
                return basketSmallBasketChargeThreshold || 0
            case THRESHOLD_SOURCE.BASKET_LIMIT:
                return 0
            case THRESHOLD_SOURCE.CUSTOM:
                return basketThreshold.thresholdValue || 0
            default:
                return 0
        }
    }

    // Overrides the threshold text with the values from the shipping method if the threshold is a delivery threshold based on the type of threshold
    function overrideText(basketThreshold: BasketThreshold) {
        type ThresholdTextKey = 'popoverTitle' | 'popoverText' | 'completedText' | 'progressText'
        const thresholdText: Record<ThresholdTextKey, string> = {
            popoverTitle: basketThreshold.popoverTitle || '',
            popoverText: basketThreshold.popoverText || '',
            completedText: basketThreshold.completedText || '',
            progressText: basketThreshold.progressText || ''
        }
        const thresholdTextKeys = Object.keys(thresholdText) as ThresholdTextKey[]

        const price =
            (basketThreshold.remaining || 0) < 1
                ? `${((basketThreshold.remaining || 0) * 100).toFixed(0)}p`
                : `£${(basketThreshold.remaining || 0).toFixed(2)}`
        const formatMoneyForCopy = (value: number) => value.toFixed(2).replace(/\.00/, '')

        thresholdTextKeys.forEach(function (textKey) {
            if (!thresholdText[textKey]) {
                return
            }

            const replacements: Record<string, string> = {
                '{charge}': formatMoneyForCopy(basketDeliveryCharge || 0),
                '{freeDeliveryThreshold}': formatMoneyForCopy(basketFreeDeliveryThreshold || 0),
                '{smallBasketChargeThreshold}': formatMoneyForCopy(basketSmallBasketChargeThreshold || 0),
                '{smallBasketCharge}': formatMoneyForCopy(basketSmallBasketCharge || 0),
                '{minBasketValue}': formatMoneyForCopy(basketMinDeliveryThreshold || 0),
                '{amt}': price,
                '{maxItems}': formatMoneyForCopy(basketMaxItemsThreshold || 0),
                '{maxWeight}': formatMoneyForCopy(basketMaxWeightThreshold || 0),
            }

            Object.keys(replacements).forEach(function (placeholder) {
                thresholdText[textKey] = thresholdText[textKey].replace(placeholder, replacements[placeholder])
            })
        })

        return thresholdText
    }

    const buildLimitThresholdItem = (basketThreshold: BasketThreshold) => {
        basketThreshold.progress = limitThreshold?.progress
        basketThreshold.complete = (basketThreshold.progress === 100)
        basketThreshold.remaining = (100 - (basketThreshold.progress || 0))
        basketThreshold.completedText = ''

        basketThreshold.thresholdValue = 16

        basketThreshold.barColour = 'accent5'

        if (basketThreshold.progress === 100) {
            basketThreshold.barText = `Checkout now to secure your ${isRapidSlot ? 'Rapid ' : ''}delivery`
            basketThreshold.completedText = 'Your Basket is full'
        } else if (basketThreshold.progress > 100) {
            basketThreshold.barText = `Basket full - try switching items to reach £${basketMinDeliveryThreshold}`
            basketThreshold.progressText = 'Your Basket is full'
            basketThreshold.barColour = 'warning'
        } else if (basketThreshold.progress > 80) {
            basketThreshold.progressText = 'Your Basket is almost full'
        } else {
            basketThreshold.progressText = 'Room for more Items'
        }
    }

    const buildThresholdItem = (basketThreshold: BasketThreshold) => {
        if (!basketThreshold.ID) {
            return null
        }

        const newThresholdItem: BasketThreshold = {
            ...basketThreshold,
            remaining: 0,
            complete: false,
            progress: 0,
        }

        newThresholdItem.thresholdValue = getThresholdValue(basketThreshold)

        if (newThresholdItem.thresholdSource !== THRESHOLD_SOURCE.BASKET_LIMIT && (!newThresholdItem.thresholdValue || newThresholdItem.thresholdValue <= 0)) {
            return null
        }

        // Handle basket limit threshold separately
        if (newThresholdItem.thresholdSource === THRESHOLD_SOURCE.BASKET_LIMIT) {
            buildLimitThresholdItem(newThresholdItem)
        } else {
            newThresholdItem.progress = Math.min((100 / newThresholdItem.thresholdValue) * basketProductTotal, 100)
            newThresholdItem.complete = newThresholdItem.progress === 100

            if (newThresholdItem.complete && !newThresholdItem.showOnComplete) {
                return null
            }

            newThresholdItem.remaining = newThresholdItem.thresholdValue - basketProductTotal
        }

        const thresholdText = overrideText(newThresholdItem)
        newThresholdItem.popoverTitle = thresholdText.popoverTitle
        newThresholdItem.popoverText = thresholdText.popoverText
        newThresholdItem.completedText = thresholdText.completedText
        newThresholdItem.progressText = thresholdText.progressText

        return newThresholdItem
    }

    const basketThresholds = useMemo(() => {
        const thresholds = [] as BasketThreshold[]

        if (basketStore.basketThresholds?.length) {
            for (const thresholdItem of basketStore.basketThresholds) {
                const thresholdObject = buildThresholdItem(thresholdItem)
                if (thresholdObject != null) {
                    thresholds.push(thresholdObject)
                }
            }

            thresholds.sort((a, b) => (a.thresholdValue > b.thresholdValue ? 1 : -1))

            let lastRelevantMessageIndex = 0
            const nonBasketLimitThresholds = thresholds.filter((t) => t.thresholdSource !== THRESHOLD_SOURCE.BASKET_LIMIT)
            for (let i = 0; i < nonBasketLimitThresholds.length; i++) {
                lastRelevantMessageIndex = i
                if (!nonBasketLimitThresholds[i].complete) {
                    break
                }
            }

            const result = nonBasketLimitThresholds.slice(0, lastRelevantMessageIndex + 1)

            // If min delivery is met (or absent) so checkout is available, always add basket limit threshold if applicable
            const minDelivery = result.find((t) => t.thresholdSource === THRESHOLD_SOURCE.MIN_DELIVERY)
            const basketLimit = thresholds.find((t) => t.thresholdSource === THRESHOLD_SOURCE.BASKET_LIMIT)
            if ((!minDelivery || minDelivery.complete) && basketLimit?.progress) {
                result.push(basketLimit)
            }

            return result
        }

        return [] as BasketThreshold[]
    }, [
        basketProductTotal,
        basketStore.basketThresholds,
        basketDeliveryCharge,
        basketFreeDeliveryThreshold,
        basketSmallBasketChargeThreshold,
        basketSmallBasketCharge,
        basketMinDeliveryThreshold,
        limitThreshold,
    ])

    const basketMaxItems = useMemo(() => {
        if (!basketThresholds.length) {
            return null
        }
        const itemTotals = basketThresholds
            .map((threshold) => threshold.basketItemTotal)
            .filter((total): total is number => total !== null && total !== undefined)

        if (itemTotals.length === 0) {
            return null
        }

        return Math.max(...itemTotals)
    }, [basketThresholds])

    return {
        basketThresholds,
        basketMaxItems,
    }
}

export default useThresholds